import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { FormControl } from '@mui/material';
import { isValid } from 'date-fns';
import React from 'react';
import { DateTimePicker } from '../DateTimePicker';
import { buildEventTab, buildGeneralTab, buildHiddenTab, buildStyleTab, buildValidateTab, showWidgetEditDialog } from '../WidgetEditDialog';
import { FormHelperErrorDescription } from '../WidgetForm/Components';
import { defaultWidgetLabelProvider, getLabelAndValuePairs, getVisibleError, handleWidgetUpdate, isSkipValidate, joinLabels, makeNewConfigUnique, renderForm, validateCustom, } from '../WidgetForm/Utils';
import { getFormLabelSuffix } from '../WidgetForm/Utils/getFormLabelSuffix';
import { convertDateToValue, convertValueToDate } from '../WidgetFormDatePicker/Utils/date';
import { type } from './types';
const WidgetFormDateTimePicker = (props) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
    const value = (_a = props.internalData.value) !== null && _a !== void 0 ? _a : { date: convertValueToDate(props.getData()) };
    const error = getVisibleError(props);
    const tabIndex = props.editMode ? -1 : undefined;
    const formLabelSuffix = getFormLabelSuffix(props);
    const dateLabel = (_c = (_b = props.config.dateLabel) !== null && _b !== void 0 ? _b : props.config.label) !== null && _c !== void 0 ? _c : 'Date';
    const timeLabel = (_e = (_d = props.config.timeLabel) !== null && _d !== void 0 ? _d : props.config.label) !== null && _e !== void 0 ? _e : 'Time';
    const readOnly = props.config.readOnly || ((_f = props.rootParams.formParams) === null || _f === void 0 ? void 0 : _f.readOnly);
    const ariaDescribedBy = React.useId();
    return (React.createElement(FormControl, { fullWidth: true, required: (_g = props.config.validate) === null || _g === void 0 ? void 0 : _g.required, error: !!error, disabled: props.config.disabled },
        React.createElement(DateTimePicker, { dateLabel: formLabelSuffix ? dateLabel + formLabelSuffix : dateLabel, timeLabel: formLabelSuffix ? timeLabel + formLabelSuffix : timeLabel, minDate: convertValueToDate((_h = props.config.validate) === null || _h === void 0 ? void 0 : _h.minDate), maxDate: convertValueToDate((_j = props.config.validate) === null || _j === void 0 ? void 0 : _j.maxDate), timeFormat: props.config.timeFormat, timeMinuteStep: props.config.timeMinuteStep, required: (_k = props.config.validate) === null || _k === void 0 ? void 0 : _k.required, disabled: props.config.disabled, readOnly: readOnly, fullWidth: true, display: props.config.display, error: !!error, dateTabIndex: tabIndex, timeTabIndex: tabIndex, value: value, onChange: (newValue) => {
                if (readOnly) {
                    return;
                }
                props.setData(convertDateToValue(newValue.date, props.config.isoString));
                props.internalData.value = newValue;
                renderForm(props, { changed: true, autoSaveDebounce: props.config.autoSave });
            }, "data-test-date": 'date-picker', "data-test-time": 'time-picker', autoFocus: (_l = (!props.editMode && props.config.autoFocus)) !== null && _l !== void 0 ? _l : false, ariaDescribedBy: ariaDescribedBy }),
        React.createElement(FormHelperErrorDescription, { descriptionId: ariaDescribedBy, error: error, description: props.config.description })));
};
const WidgetFormDateEditor = (props) => {
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props, { widgetLabelProvider: defaultWidgetLabelProvider, onEditWidget: (endEditing) => showWidgetEditDialog(props, endEditing, { title: 'Date Time Picker: ' + props.config.label }, buildGeneralTab(props, {
            key: true,
            label: true,
            appendRowIndexToLabel: true,
            description: true,
            autoFocus: true,
            onLoad: (data) => {
                if (!data.timeFormat) {
                    data.timeFormat = '24h';
                }
                if (data.timeMinuteStep == null) {
                    data.timeMinuteStep = 30;
                }
                return data;
            },
            children: [
                {
                    type: 'form.radio',
                    key: 'timeFormat',
                    label: 'Time Format',
                    values: [
                        { value: '24h', label: '24h' },
                        { value: '12h', label: '12h (using AM/PM)' },
                    ],
                    style: {
                        inline: true,
                    },
                },
                {
                    type: 'form.radio',
                    key: 'timeMinuteStep',
                    label: 'Step Over Minutes',
                    values: [
                        { value: 1, label: '1' },
                        { value: 5, label: '5' },
                        { value: 10, label: '10' },
                        { value: 15, label: '15' },
                        { value: 30, label: '30' },
                        { value: 60, label: '60' },
                    ],
                    style: {
                        inline: true,
                    },
                },
                {
                    type: 'form.checkbox',
                    key: 'isoString',
                    label: 'Store Value As ISO String',
                },
            ],
        }), buildValidateTab(props, {
            required: true,
            children: [
                {
                    type: 'form.dateTimePicker',
                    key: 'validate.minDate',
                    label: 'Min Date',
                },
                {
                    type: 'form.text',
                    key: 'validate.minDateMessage',
                    label: 'Min Date Message',
                    emptyAsNull: true,
                },
                {
                    type: 'form.dateTimePicker',
                    key: 'validate.maxDate',
                    label: 'Max Date',
                },
                {
                    type: 'form.text',
                    key: 'validate.maxDateMessage',
                    label: 'Max Date Message',
                    emptyAsNull: true,
                },
            ],
        }), buildHiddenTab(props), buildStyleTab(props), buildEventTab(props, { eventOnChange: true, autoSave: true })) }),
        React.createElement(WidgetFormDateTimePicker, Object.assign({}, props))));
};
export const widgetFormDateTimePicker = {
    type,
    validate: (props) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        if (isSkipValidate(props)) {
            return;
        }
        if (((_b = (_a = props.internalData.value) === null || _a === void 0 ? void 0 : _a.dateValue) === null || _b === void 0 ? void 0 : _b.invalidDate) != null) {
            return 'Invalid date';
        }
        const value = props.getData();
        if (value != null) {
            const date = convertValueToDate(value);
            if (date && isValid(date)) {
                const minDate = convertValueToDate((_c = props.config.validate) === null || _c === void 0 ? void 0 : _c.minDate);
                const maxDate = convertValueToDate((_d = props.config.validate) === null || _d === void 0 ? void 0 : _d.maxDate);
                if (minDate && isValid(minDate) && date < minDate) {
                    return ((_f = (_e = props.config.validate) === null || _e === void 0 ? void 0 : _e.minDateMessage) !== null && _f !== void 0 ? _f : props.config.label + ' cannot be before ' + minDate.toLocaleDateString()); // TODO format
                }
                if (maxDate && isValid(minDate) && date > maxDate) {
                    return (_h = (_g = props.config.validate) === null || _g === void 0 ? void 0 : _g.maxDateMessage) !== null && _h !== void 0 ? _h : props.config.label + ' cannot be after ' + maxDate.toLocaleDateString(); // TODO format
                }
            }
            else {
                return 'Invalid date';
            }
        }
        else if (((_j = props.config.validate) === null || _j === void 0 ? void 0 : _j.required) && value == null) {
            if (props.config.dateLabel && props.config.timeLabel) {
                return props.config.dateLabel + ' and ' + props.config.timeLabel + ' are required';
            }
            else {
                return (_k = props.config.validate.requiredMessage) !== null && _k !== void 0 ? _k : (props.config.label ? props.config.label + ' is required' : 'Required');
            }
        }
        return validateCustom(props, value);
    },
    update: handleWidgetUpdate,
    getLabelValueAsTextPairs: (props, labelPrefix) => {
        const value = props.getData();
        if (value != null) {
            const date = convertValueToDate(value);
            if (date) {
                return getLabelAndValuePairs(joinLabels(labelPrefix, props.config.label), date.toLocaleDateString()); // TODO format
            }
        }
        return [];
    },
    component: WidgetFormDateTimePicker,
    icon: React.createElement(CalendarMonthIcon, { fontSize: 'small' }),
    editorComponent: WidgetFormDateEditor,
    createWidget: (rootConfig, editorPath, widgets) => makeNewConfigUnique(rootConfig, editorPath, widgets, { type, key: 'dateTimePicker', label: 'Date Time Picker' }),
};
